import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Plus, FileDown, Search, ShieldCheck, ShieldAlert, BarChart3, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUT_COLORS = {
  recu: "text-sky-600 dark:text-sky-400",
  diagnostic: "text-amber-600 dark:text-amber-400",
  envoye_fournisseur: "text-amber-600 dark:text-amber-400",
  repare: "text-emerald-600 dark:text-emerald-400",
  echange: "text-emerald-600 dark:text-emerald-400",
  rembourse: "text-emerald-600 dark:text-emerald-400",
  refuse: "text-destructive",
  cloture: "text-muted-foreground",
};

export default function SAVListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");
  const [enCoursSeulement, setEnCoursSeulement] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const load = () => api.get(`/sav${enCoursSeulement ? "?en_cours_seulement=true" : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [enCoursSeulement]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/sav/statuts").then((r) => setStatuts(r.data)).catch(() => {}); }, []);

  const statutLabel = (key) => statuts.find((s) => s.key === key)?.label || key;

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (q && !((it.numero || "").toLowerCase().includes(q) || (it.client_nom || "").toLowerCase().includes(q) || (it.produit_nom || "").toLowerCase().includes(q))) return false;
    if (statutFilter !== "all" && it.statut !== statutFilter) return false;
    return true;
  });

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce dossier SAV ?")) return;
    try {
      await api.delete(`/sav/${id}`);
      toast.success("Dossier SAV supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const ouvrirStats = async () => {
    setStatsOpen(true);
    try {
      const { data } = await api.get("/sav/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="SAV">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto flex-wrap">
          <div className="relative w-full sm:max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="sav-search-input" placeholder="Rechercher par n°, client, produit..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={statutFilter} onValueChange={setStatutFilter}>
            <SelectTrigger data-testid="sav-statut-filter" className="w-full sm:w-[220px] shrink-0">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {statuts.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={enCoursSeulement ? "default" : "outline"} size="sm"
            onClick={() => setEnCoursSeulement((v) => !v)} data-testid="sav-en-cours-toggle"
          >
            En cours seulement
          </Button>
        </div>
        <div className="flex gap-2 shrink-0 w-full sm:w-auto">
          {hasPerm(user, "sav", "stats") && (
            <Button variant="outline" onClick={ouvrirStats} className="gap-2" data-testid="sav-stats-button">
              <BarChart3 size={16} /> Statistiques
            </Button>
          )}
          {hasPerm(user, "sav", "create") && (
            <Link to="/sav/new" className="w-full sm:w-auto">
              <Button data-testid="sav-add-button" className="gap-2 w-full sm:w-auto"><Plus size={16} /> Nouveau dossier SAV</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="sav-list">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="p-3">N°</th><th className="p-3">Client</th><th className="p-3">Produit</th>
              <th className="p-3">Garantie</th><th className="p-3">Statut</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`sav-row-${it.id}`}>
                <td className="p-3">{it.numero}</td>
                <td className="p-3">{it.client_nom}</td>
                <td className="p-3">{it.produit_nom}</td>
                <td className="p-3" data-testid={`sav-garantie-${it.id}`}>
                  {it.sous_garantie === true && (
                    <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                      <ShieldCheck size={13} /> {it.garantie_jours_restants} j restants
                    </span>
                  )}
                  {it.sous_garantie === false && (
                    <span className="flex items-center gap-1.5 text-destructive text-xs font-medium">
                      <ShieldAlert size={13} /> Expirée
                    </span>
                  )}
                  {it.sous_garantie == null && <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className={`p-3 text-xs font-medium ${STATUT_COLORS[it.statut] || ""}`}>{statutLabel(it.statut)}</td>
                <td className="p-3 text-right">
                  <div className="flex justify-end items-center gap-3">
                    <Link to={`/sav/${it.id}`} data-testid={`sav-open-${it.id}`} className="text-primary text-sm">
                      {hasPerm(user, "sav", "edit") ? "Modifier" : "Voir"}
                    </Link>
                    <a href={`${api.defaults.baseURL}/sav/${it.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`sav-pdf-${it.id}`} title="PDF"><FileDown size={15} /></a>
                    {hasPerm(user, "sav", "delete") && (
                      <button data-testid={`sav-delete-${it.id}`} onClick={() => remove(it.id)} title="Supprimer" className="text-destructive"><Trash2 size={15} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">Aucun dossier SAV.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {statsOpen && stats && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setStatsOpen(false)}>
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sav-stats-panel">
            <h3 className="font-semibold mb-4">Statistiques SAV</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg bg-secondary p-3">
                <p className="text-xs text-muted-foreground">Total dossiers</p>
                <p className="text-2xl font-bold">{stats.total_dossiers}</p>
              </div>
              <div className="rounded-lg bg-secondary p-3">
                <p className="text-xs text-muted-foreground">En cours</p>
                <p className="text-2xl font-bold">{stats.total_en_cours}</p>
              </div>
            </div>
            {stats.delai_moyen_jours != null && (
              <p className="text-sm mb-4">Délai moyen de traitement : <span className="font-semibold">{stats.delai_moyen_jours} jour(s)</span></p>
            )}
            <p className="text-sm font-medium mb-2">Répartition par produit</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {stats.par_produit.map((p) => (
                <div key={p.produit_nom} className="flex items-center justify-between text-sm">
                  <span className="truncate">{p.produit_nom}</span>
                  <span className="font-semibold shrink-0 ml-2">{p.nombre}</span>
                </div>
              ))}
              {stats.par_produit.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
            </div>
            <Button className="w-full mt-4" onClick={() => setStatsOpen(false)}>Fermer</Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
